<template>
    <!-- 홈 상단 요약 4칸 — 오늘 일정(/shelter/schedule 오늘 하루) + 상담중인 동물·오늘 접수된 입양신청서·추천동물 현황(/shelter/home/summary) -->
    <div class="hs">
        <!-- 오늘 일정 -->
        <div class="hs-card" style="--a: #1FA971; --a-bg: #E8F7F0" role="button" tabindex="0" title="오늘 일정 크게 보기" @click="goTodayCalendar()" @keyup.enter="goTodayCalendar()">
            <div class="hs-head"><span class="hs-icon"><i class="bi bi-calendar-check"></i></span><span class="hs-title">오늘 일정</span><i class="bi bi-chevron-right hs-go"></i></div>
            <div class="hs-main"><span class="hs-num">{{ todayLeft.length }}</span><span class="hs-unit">건</span></div>
            <div v-if="!todayLoaded" class="hs-empty">불러오는 중…</div>
            <div v-else-if="!todayLeft.length" class="hs-empty">{{ todayDoneCnt ? '오늘 일정을 모두 마쳤어요' : '오늘은 일정이 없어요' }}</div>
            <div v-else class="hs-list">
                <div v-for="e in todayLeft.slice(0, PREVIEW)" :key="e.type + e.id" class="hs-row">
                    <span class="hs-dot" :style="{ background: colorOf(e) }"></span>
                    <span class="hs-time">{{ e.time || '종일' }}</span>
                    <span class="hs-text">{{ eventText(e) }}</span>
                </div>
                <div v-if="todayLeft.length > PREVIEW" class="hs-more">외 {{ todayLeft.length - PREVIEW }}건</div>
            </div>
        </div>

        <!-- 상담중인 동물 -->
        <div class="hs-card" style="--a: #3B82F6; --a-bg: #EBF2FE" role="button" tabindex="0" title="입양신청서 관리로" @click="go('/adoption')" @keyup.enter="go('/adoption')">
            <div class="hs-head"><span class="hs-icon"><i class="bi bi-chat-heart"></i></span><span class="hs-title">상담중인 동물</span><i class="bi bi-chevron-right hs-go"></i></div>
            <div class="hs-main"><span class="hs-num">{{ consulting.animal_cnt || 0 }}</span><span class="hs-unit">마리</span></div>
            <div v-if="!loaded" class="hs-empty">불러오는 중…</div>
            <div v-else-if="!(consulting.items || []).length" class="hs-empty">상담중인 동물이 없어요</div>
            <div v-else class="hs-animals">
                <div v-for="a in (consulting.items || []).slice(0, 3)" :key="a.notify_number" class="hs-animal" :title="(a.animal_name || '') + ' ' + a.notify_number" @click.stop="go('/adoption', { notify: a.notify_number })">
                    <img :src="photoOf(a)" alt="" @error="onImgError"/>
                    <span class="hs-animal-name">{{ a.animal_name || shortNotify(a.notify_number) }}</span>
                    <span v-if="a.cnt > 1" class="hs-badge">{{ a.cnt }}</span>
                </div>
                <div v-if="(consulting.animal_cnt || 0) > 3" class="hs-animal"><span class="hs-animal-more">+{{ consulting.animal_cnt - 3 }}</span></div>
            </div>
        </div>

        <!-- 오늘 접수된 입양신청서 -->
        <div class="hs-card" style="--a: #8B5CF6; --a-bg: #F3EEFE" role="button" tabindex="0" title="입양신청서 관리로" @click="go('/adoption')" @keyup.enter="go('/adoption')">
            <div class="hs-head"><span class="hs-icon"><i class="bi bi-file-earmark-text"></i></span><span class="hs-title">{{ isMobile ? '오늘 접수 신청서' : '오늘 접수된 입양신청서' }}</span><i class="bi bi-chevron-right hs-go"></i></div>
            <div class="hs-main"><span class="hs-num">{{ todayReq.cnt || 0 }}</span><span class="hs-unit">건</span></div>
            <div v-if="!loaded" class="hs-empty">불러오는 중…</div>
            <div v-else-if="!(todayReq.items || []).length" class="hs-empty">오늘 들어온 신청서가 없어요</div>
            <div v-else class="hs-list">
                <div v-for="r in (todayReq.items || []).slice(0, PREVIEW)" :key="r.request_idx" class="hs-row hs-link" @click.stop="go('/adoption', { request: r.request_idx })">
                    <span class="hs-time">{{ r.time }}</span>
                    <span class="hs-text">{{ r.applicant || '신청자' }}<span class="hs-faint"> · {{ r.animal_name || shortNotify(r.notify_number) }}</span></span>
                    <span class="badge hs-state" :class="stateClass(r.state)">{{ r.state }}</span>
                </div>
                <div v-if="todayReq.cnt > PREVIEW" class="hs-more">+ {{ todayReq.cnt - Math.min(PREVIEW, (todayReq.items || []).length) }}건</div>
            </div>
        </div>

        <!-- 추천동물 현황 -->
        <div class="hs-card" style="--a: #F6951C; --a-bg: #FFF3E3" role="button" tabindex="0" title="동물 관리로" @click="go('/animal')" @keyup.enter="go('/animal')">
            <div class="hs-head"><span class="hs-icon"><svg class="hs-paw" viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="6.2" cy="9.6" rx="2.1" ry="2.6"/><ellipse cx="10.1" cy="5.9" rx="2.2" ry="2.8"/><ellipse cx="14.9" cy="5.9" rx="2.2" ry="2.8"/><ellipse cx="18.8" cy="9.6" rx="2.1" ry="2.6"/><path d="M12.5 11.2c-2.6 0-6 3.4-6 6.1 0 1.6 1.2 2.5 2.7 2.5 1.2 0 2.1-.6 3.3-.6s2.1.6 3.3.6c1.5 0 2.7-.9 2.7-2.5 0-2.7-3.4-6.1-6-6.1z"/></svg></span><span class="hs-title">추천동물 현황</span><i class="bi bi-chevron-right hs-go"></i></div>
            <div class="hs-main"><span class="hs-num">{{ rec.cnt || 0 }}</span><span class="hs-unit">{{ rec.limit ? '/ ' + rec.limit + '마리' : '마리' }}</span></div>
            <div v-if="!loaded" class="hs-empty">불러오는 중…</div>
            <div v-else-if="rec.active && rec.active != 'ON'" class="hs-empty">추천 기능을 쓰지 않는 보호소예요</div>
            <div v-else-if="!(rec.items || []).length" class="hs-empty">추천 중인 동물이 없어요</div>
            <div v-else class="hs-animals">
                <div v-for="a in rec.items.slice(0, 3)" :key="a.notify_number" class="hs-animal" :title="(a.animal_name || '') + ' ' + a.notify_number + ' · 신청서 ' + (a.req_cnt || 0) + '건'" @click.stop="go('/animal', { notify: a.notify_number })">
                    <img :src="photoOf(a)" alt="" @error="onImgError"/>
                    <span class="hs-animal-name">{{ a.animal_name || shortNotify(a.notify_number) }}</span>
                    <span v-if="a.req_cnt" class="hs-badge">{{ a.req_cnt }}</span>
                </div>
                <div v-if="(rec.cnt || 0) > 3" class="hs-animal"><span class="hs-animal-more">+{{ rec.cnt - 3 }}</span></div>
            </div>
        </div>
    </div>
</template>

<script>
import { scheduleColor } from '@/utils/scheduleStore';
const moment = require('moment');

export default {
    name: 'HomeSummary',
    props: { shelter: { type: Object, default: () => ({}) } },
    data() {
        return {
            isMobile: false,   //768px 이하: 미리보기 2개
            todayEvents: [], todayLoaded: false,
            consulting: {}, todayReq: {}, rec: {}, loaded: false,
            seq: 0,
        };
    },
    computed: {
        PREVIEW() { return this.isMobile ? 2 : 3; },
        //캘린더와 같게 공고 종료·입양신청은 빼고, 종일 먼저 그다음 시간순
        todayVisible() {
            return this.todayEvents.filter(e => e.type != 'notice_end' && e.type != 'adopt')
                .sort((a, b) => (a.time ? 1 : 0) - (b.time ? 1 : 0) || (a.time || '').localeCompare(b.time || ''));
        },
        todayLeft() { return this.todayVisible.filter(e => e.done != 'Y'); },
        todayDoneCnt() { return this.todayVisible.length - this.todayLeft.length; },
    },
    watch: {
        'shelter.shelter_device_number'() { this.load(); },
    },
    methods: {
        load() {
            const shelter = this.shelter && this.shelter.shelter_device_number;
            if (!shelter) return;
            const seq = ++this.seq;
            const today = moment().format('YYYY-MM-DD');
            const q = encodeURIComponent(shelter);
            this.axios.get(`/shelter/schedule?start=${today}&end=${today}&shelter_device_number=${q}`)
                .then(res => { if (seq == this.seq) this.todayEvents = (res.data && res.data.events) || []; })
                .catch(e => console.log('오늘 일정 조회 실패', e))
                .finally(() => { if (seq == this.seq) this.todayLoaded = true; });
            this.axios.get(`/shelter/home/summary?shelter_device_number=${q}`)
                .then(res => {
                    if (seq != this.seq || !res.data) return;
                    this.consulting = res.data.consulting || {};
                    this.todayReq = res.data.today_requests || {};
                    this.rec = res.data.recommended || {};
                })
                .catch(e => console.log('홈 요약 조회 실패', e))
                .finally(() => { if (seq == this.seq) this.loaded = true; });
        },
        colorOf(e) { return scheduleColor(e); },
        eventText(e) {
            const animal = e.animal_name || (e.notify_number ? this.shortNotify(e.notify_number) : '');
            if (e.type == 'vaccine' || e.type == 'kit') return (animal ? animal + ' ' : '') + (e.title || '');
            return e.title || '';
        },
        //입양신청서 관리 목록 뱃지와 같은 색 (AdoptionManage setStateStyle)
        stateClass(s) {
            return { '입양완료': 'bg-orange', '대기중': 'bg-warning', '상담중': 'bg-primary', '반려': 'bg-secondary', '신청취소': 'bg-secondary', '종료': 'bg-secondary' }[s] || 'bg-secondary';
        },
        shortNotify(n) { return n ? String(n).split('-').slice(-1)[0] : ''; },
        photoOf(a) { return a.more_image1 ? this.$common.showImage('shelter/' + a.more_image1) : (a.image || require('@/assets/img/image_error_icon.png')); },
        onImgError(ev) { ev.target.src = require('@/assets/img/image_error_icon.png'); },
        go(path, query) { this.$router.push({ path, query: query || {} }); },
        goTodayCalendar() { this.emitter.emit('homeCalendarGoDay', { date: moment().format('YYYY-MM-DD') }); },
        onScheduleChanged() { this.load(); },
        onMedia() { this.isMobile = !!(this.mq && this.mq.matches); },
    },
    mounted() {
        this.mq = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
        this.onMedia();
        if (this.mq) (this.mq.addEventListener ? this.mq.addEventListener('change', this.onMedia) : this.mq.addListener(this.onMedia));
        this.load();
        this.emitter.on('scheduleChanged', this.onScheduleChanged);
    },
    beforeUnmount() {
        if (this.mq) (this.mq.removeEventListener ? this.mq.removeEventListener('change', this.onMedia) : this.mq.removeListener(this.onMedia));
        this.emitter.off('scheduleChanged', this.onScheduleChanged);
    },
};
</script>

<style scoped>
    .hs { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-top: 1.6rem; text-align: left; }
    .hs-card { background: #fff; border-radius: 14px; padding: 14px 16px 14px; box-shadow: 0 1px 3px rgba(0, 0, 0, .06); border: 1px solid #eef0f2; cursor: pointer; display: flex; flex-direction: column; min-width: 0; transition: box-shadow .15s, transform .15s; }
    .hs-card:hover { box-shadow: 0 6px 18px rgba(0, 0, 0, .08); transform: translateY(-1px); }
    .hs-card:focus-visible { outline: 2px solid var(--a); outline-offset: 2px; }
    .hs-head { display: flex; align-items: center; gap: 8px; }
    .hs-icon { width: 28px; height: 28px; border-radius: 8px; background: var(--a-bg); color: var(--a); display: inline-flex; align-items: center; justify-content: center; font-size: 15px; flex: 0 0 auto; }
    .hs-paw { width: 16px; height: 16px; fill: currentColor; }
    .hs-title { font-size: 14px; font-weight: 700; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .hs-go { margin-left: auto; color: #c2c2c2; font-size: 13px; }
    .hs-card:hover .hs-go { color: var(--a); }
    .hs-main { display: flex; align-items: baseline; gap: 4px; margin: 10px 0 8px; flex-wrap: wrap; }
    .hs-num { font-size: 28px; font-weight: 800; color: #222; line-height: 1; }
    .hs-unit { font-size: 14px; font-weight: 700; color: #666; }
    .hs-empty { font-size: 13px; color: #a4a4a4; padding: 6px 0 2px; }
    .hs-list { display: flex; flex-direction: column; gap: 4px; }
    .hs-row { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #333; min-width: 0; padding: 3px 4px; margin: 0 -4px; border-radius: 6px; }
    .hs-link:hover { background: var(--a-bg); }
    .hs-dot { width: 7px; height: 7px; border-radius: 50%; flex: 0 0 auto; }
    .hs-time { flex: 0 0 auto; font-variant-numeric: tabular-nums; color: #777; font-weight: 600; min-width: 34px; }
    .hs-text { flex: 1 1 auto; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .hs-faint { color: #999; }
    .hs-state { flex: 0 0 auto; font-size: 11px; font-weight: 700; padding: 3px 6px; line-height: normal; }
    .hs-more { font-size: 12px; color: #999; padding-left: 2px; }
    .hs-animals { display: flex; gap: 10px; flex-wrap: nowrap; }
    .hs-animal-more { width: 46px; height: 46px; border-radius: 50%; background: var(--a-bg); color: var(--a); font-size: 13px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; }
    .hs-animal { position: relative; display: flex; flex-direction: column; align-items: center; gap: 4px; width: 54px; }
    .hs-animal img { width: 46px; height: 46px; border-radius: 50%; object-fit: cover; background: #f3f3f3; border: 2px solid #fff; box-shadow: 0 0 0 1px #e8e8e8; transition: box-shadow .15s; }
    .hs-animal:hover img { box-shadow: 0 0 0 2px var(--a); }
    .hs-animal-name { font-size: 11.5px; color: #555; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .hs-badge { position: absolute; top: -2px; right: 2px; min-width: 18px; height: 18px; border-radius: 9px; background: var(--a); color: #fff; font-size: 10.5px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; padding: 0 4px; border: 2px solid #fff; }

    @media (max-width: 1100px) {
        .hs { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    /* 모바일: 2×2, 숫자·목록 간단히 */
    @media (max-width: 768px) {
        .hs { gap: 10px; margin-top: 1.2rem; }
        .hs-card { padding: 12px 12px; border-radius: 12px; }
        .hs-title { font-size: 13px; }
        .hs-icon { width: 24px; height: 24px; font-size: 13px; }
        .hs-num { font-size: 24px; }
        .hs-main { margin: 8px 0 6px; }
        .hs-row { font-size: 12.5px; }
        .hs-animals { gap: 6px; }
        .hs-animal { width: 42px; }
        .hs-animal img { width: 36px; height: 36px; }
        .hs-animal-more { width: 36px; height: 36px; font-size: 12px; }
    }
</style>
